import { RestRequest, RestContext, ResponseFunction } from 'msw'
import { requireAuthenticatedUser } from '../decorators'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from './mockDataCommon'
import {
  isOverRequestBodyLimit,
  requestTooLargeBody,
} from './requestTooLarge'
import type { TEntityDocument } from '~/types/document/models'

export function makeDocumentAPIs<T extends TEntityDocument>({
  apiPathEntityNamePrefix,
  getDocumentListDataFn,
}: {
  apiPathEntityNamePrefix: string
  getDocumentListDataFn: () => Promise<T[]>
}) {
  interface GetDocumentsResponse {
    data: T[]
  }

  async function getDocuments(
    req: RestRequest,
    res: ResponseFunction,
    ctx: RestContext
  ) {
    const responseBody: GetDocumentsResponse = {
      data: await getDocumentListDataFn(),
    }
    return res(ctx.status(200), ctx.json(responseBody))
  }

  // post

  const postDocument = async (
    req: RestRequest,
    res: ResponseFunction,
    ctx: RestContext
  ) => {
    // The real API caps the request body and rejects with 413 before reading it.
    // Without this the mock accepted any size, so no test could reach the 413
    // path at all -- see requestTooLarge.ts.
    if (isOverRequestBodyLimit(req)) {
      return res(ctx.status(413), ctx.json(requestTooLargeBody()))
    }
    return addToEntityListData(req, res, ctx, getDocumentListDataFn)
  }

  const patchDocument = async (
    req: RestRequest,
    res: ResponseFunction,
    ctx: RestContext
  ) => {
    return updateEntityListData(req, res, ctx, getDocumentListDataFn)
  }

  const deleteDocument = async (
    req: RestRequest,
    res: ResponseFunction,
    ctx: RestContext
  ) => {
    return deleteEntityListData(req, res, ctx, getDocumentListDataFn)
  }

  const handlers = [
    {
      method: `get`,
      path: `/api/${apiPathEntityNamePrefix}documents`,
      handler: getDocuments,
    },
    {
      method: `post`,
      path: `/api/${apiPathEntityNamePrefix}documents`,
      handler: postDocument,
    },
    {
      method: `patch`,
      path: `/api/${apiPathEntityNamePrefix}documents/:id`,
      handler: patchDocument,
    },
    {
      method: `delete`,
      path: `/api/${apiPathEntityNamePrefix}documents/:id`,
      handler: deleteDocument,
    },
  ].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))

  return { getDocuments, postDocument, patchDocument, deleteDocument, handlers }
}
